import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Table, Button, LoadingSkeleton, Modal, ToastContainer } from './ui';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';
import { hasAnyRole } from '../utils/roles';

const WRITE_ROLES = ['administrator', 'principal', 'head_of_academics'];

const EMPTY_FORM = { subject_id: '', teacher_id: '', class_id: '', term_id: '' };

function SubjectAllocations({ token, user }) {
  const canWrite = hasAnyRole(user, WRITE_ROLES);
  const [searchParams] = useSearchParams();
  const teacherFilter = searchParams.get('teacher_id') || '';

  const [allocations, setAllocations] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchData = useCallback(async () => {
    try {
      const allocationsUrl = teacherFilter ? `/api/v1/subject-allocations?teacher_id=${teacherFilter}` : '/api/v1/subject-allocations';
      const [allocationsRes, subjectsRes, teachersRes, classesRes, termsRes] = await Promise.all([
        fetch(allocationsUrl, { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/subjects', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/teachers', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/terms', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAllocations(await allocationsRes.json());
      setSubjects(await subjectsRes.json());
      setTeachers(await teachersRes.json());
      setClasses(await classesRes.json());
      setTerms(await termsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, teacherFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setFormData({ ...EMPTY_FORM, teacher_id: teacherFilter || '' });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/subject-allocations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setShowForm(false);
        fetchData();
      } else {
        addToast(await safeErrorMessage(response, 'Failed to create allocation'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const handleDelete = async (allocation) => {
    if (!window.confirm(`Remove ${allocation.subject_name} for ${allocation.class_display_name}?`)) return;
    try {
      const response = await fetch(`/api/v1/subject-allocations/${allocation.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        fetchData();
      } else if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        if (window.confirm(`${data.error} — remove anyway?`)) {
          const retry = await fetch(`/api/v1/subject-allocations/${allocation.id}?force=true`, {
            method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
          });
          if (retry.ok) fetchData();
          else addToast(await safeErrorMessage(retry, 'Failed to remove allocation'), 'error');
        }
      } else {
        addToast(await safeErrorMessage(response, 'Failed to remove allocation'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const columns = [
    { header: 'Subject', accessor: 'subject_name' },
    { header: 'Teacher', accessor: 'teacher_name' },
    { header: 'Class', accessor: 'class_display_name' },
  ];

  if (canWrite) {
    columns.push({
      header: 'Actions', width: '80px',
      render: (row) => (
        <Button variant="danger" size="sm" onClick={() => handleDelete(row)}>
          <TrashIcon style={{ width: 14, height: 14 }} />
        </Button>
      ),
    });
  }

  if (loading) return <LoadingSkeleton type="text" rows={10} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Subject Allocations</h2>
        {canWrite && (
          <Button variant="primary" size="sm" onClick={openCreate}>
            <PlusIcon style={{ width: 16, height: 16 }} />
            Add Allocation
          </Button>
        )}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add Subject Allocation">
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label>Subject *</label>
              <select value={formData.subject_id} onChange={(e) => setFormData({ ...formData, subject_id: e.target.value })} required>
                <option value="">Select Subject</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Teacher *</label>
              <select value={formData.teacher_id} onChange={(e) => setFormData({ ...formData, teacher_id: e.target.value })} required>
                <option value="">Select Teacher</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Class *</label>
              <select value={formData.class_id} onChange={(e) => setFormData({ ...formData, class_id: e.target.value })} required>
                <option value="">Select Class</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Term *</label>
              <select value={formData.term_id} onChange={(e) => setFormData({ ...formData, term_id: e.target.value })} required>
                <option value="">Select Term</option>
                {terms.map(t => <option key={t.id} value={t.id}>Term {t.term_number} - {t.year}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <Button type="submit" variant="primary">Add Allocation</Button>
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Card title="Subject Allocations">
        <Table columns={columns} data={allocations} emptyMessage="No subject allocations found." />
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default SubjectAllocations;
